import type { RefObject } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { CopilotChatActivityStage, KnowledgeScope } from "@yazamco/shared-types";
import { Icon } from "../../components/ui/Icon";
import { CopilotLogo } from "../../components/ui/CopilotLogo";
import { AnswerImage, normalizeCopilotAnswerUrl } from "./AnswerImage";
import { SourceList } from "./SourceList";
import { scopeLabel } from "./KnowledgeScopePicker";
import type { ChatMessage } from "./copilot-chat";

interface MessageListProps {
  messages: ChatMessage[];
  busy: boolean;
  activity: { stage: CopilotChatActivityStage; message: string } | null;
  /** Where the next question is aimed — named in the waiting indicator. */
  scope: KnowledgeScope;
  /** The answer whose "Copied" tick is currently showing, if any. */
  copiedId: number | null;
  historyRef: RefObject<HTMLDivElement>;
  onAsk: (message: string, aimedAt?: KnowledgeScope) => void;
  onCopyAnswer: (message: ChatMessage) => void;
  onEditQuestion: (text: string) => void;
  /** The question an answer belongs to: the nearest user turn before it. */
  promptBefore: (messageId: number) => string | null;
}

/**
 * The transcript, on the canvas's terms: one 740px measure, 36px between
 * turns, the answer set as the page itself — no card, no border — and the
 * user's own turn a compact tinted bubble pushed to the trailing edge.
 */
export function MessageList({
  messages,
  busy,
  activity,
  scope,
  copiedId,
  historyRef,
  onAsk,
  onCopyAnswer,
  onEditQuestion,
  promptBefore,
}: MessageListProps): JSX.Element {
  return (
    <div className="cp-thread" aria-live="polite">
      <div className="cp-measure cp-thread-inner" ref={historyRef}>
        {messages.map((message) => (
          <MessageTurn
            key={message.id}
            message={message}
            busy={busy}
            activity={activity}
            copiedId={copiedId}
            onAsk={onAsk}
            onCopyAnswer={onCopyAnswer}
            onEditQuestion={onEditQuestion}
            promptBefore={promptBefore}
          />
        ))}

        {/* Three pulsing bars under a live byline, exactly where the answer
            will appear — so nothing jumps when the first token lands. */}
        {busy && !messages.some((message) => message.streaming) && (
          <article className="cp-turn cp-turn-assistant" role="status">
            <div className="cp-byline">
              <span className="cp-copilot-mark">
                <CopilotLogo size={20} active />
              </span>
              <span className="cp-byline-name">Copilot</span>
              <span className="cp-byline-rule" />
              <span className="cp-byline-meta">
                {activity?.message ??
                  (scope.kind === "all"
                    ? "Searching your mail, Teams and files…"
                    : `Searching ${scopeLabel(scope)}…`)}
              </span>
            </div>
            <div className="cp-skeleton">
              <span />
              <span />
              <span />
            </div>
          </article>
        )}
      </div>
    </div>
  );
}

function MessageTurn({
  message,
  busy,
  activity,
  copiedId,
  onAsk,
  onCopyAnswer,
  onEditQuestion,
  promptBefore,
}: {
  message: ChatMessage;
  busy: boolean;
  activity: { stage: CopilotChatActivityStage; message: string } | null;
  copiedId: number | null;
  onAsk: (message: string, aimedAt?: KnowledgeScope) => void;
  onCopyAnswer: (message: ChatMessage) => void;
  onEditQuestion: (text: string) => void;
  promptBefore: (messageId: number) => string | null;
}): JSX.Element {
  if (message.role === "user") {
    return (
      <div className="cp-turn cp-turn-user">
        <div className="cp-bubble" dir="auto">
          {message.text}
        </div>
        {message.retryUnscoped && (
          <button
            type="button"
            className="btn btn-secondary btn-sm cp-scope-escape"
            disabled={busy}
            onClick={() => onAsk(message.retryUnscoped!, { kind: "all" })}
          >
            <Icon name="scope" size={14} /> Search all of Microsoft 365
          </button>
        )}
      </div>
    );
  }

  if (message.role === "error") {
    return (
      <div className="cp-inline-error">
        <div>
          <p className="cp-inline-error-title">Copilot couldn’t complete this request</p>
          <p>{message.text}</p>
          {message.retryUnscoped ? (
            <button
              type="button"
              className="cp-inline-error-action"
              disabled={busy}
              onClick={() => onAsk(message.retryUnscoped!, { kind: "all" })}
            >
              Search all of Microsoft 365
            </button>
          ) : (
            message.retryPrompt && (
              <button
                type="button"
                className="cp-inline-error-action"
                disabled={busy}
                onClick={() => onAsk(message.retryPrompt!)}
                data-testid="copilot-retry"
              >
                Retry
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  if (message.role === "stopped") {
    return <p className="cp-note">{message.text}</p>;
  }

  const prompt = promptBefore(message.id);

  return (
    <article className={`cp-turn cp-turn-assistant${message.streaming ? " is-streaming" : ""}`}>
      {/* While the answer is arriving the byline says what Copilot is doing,
          which is the one place a live region belongs on a streaming turn. */}
      <div className="cp-byline" role={message.streaming ? "status" : undefined}>
        <span className="cp-copilot-mark">
          <CopilotLogo size={20} active={Boolean(message.streaming)} />
        </span>
        <span className="cp-byline-name">Copilot</span>
        {(message.streaming || message.time) && (
          <>
            <span className="cp-byline-rule" />
            <span className="cp-byline-meta">
              {message.streaming ? (activity?.message ?? "Working…") : message.time}
            </span>
          </>
        )}
      </div>

      <div className="cp-answer" dir="auto">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ children, href, ...props }) => (
              <a
                {...props}
                href={href ? normalizeCopilotAnswerUrl(href) : href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
            // The canvas puts every table in its own horizontally scrolling
            // box so a wide table never pushes the page sideways.
            table: ({ children, ...props }) => (
              <div className="cp-table-wrap">
                <table {...props}>{children}</table>
              </div>
            ),
            // An answer's images are fetched through the API rather than
            // loaded from Microsoft directly — see AnswerImage for why the
            // default <img> showed nothing but the alt text.
            img: ({ src, alt, title }) => (
              <AnswerImage
                src={typeof src === "string" ? src : undefined}
                alt={alt}
                title={title}
              />
            ),
          }}
        >
          {message.text}
        </ReactMarkdown>
      </div>

      <SourceList
        attributions={message.attributions}
        retrievedSources={message.retrievedSources}
        mcpSources={message.mcpSources}
      />

      {/* Hidden while the answer is still arriving: copying half an answer, or
          re-asking before the first attempt has finished, is never what was
          meant. */}
      {!message.streaming && (
        <div className="cp-turn-actions">
          <button
            type="button"
            onClick={() => onCopyAnswer(message)}
            aria-label="Copy this answer"
            title={copiedId === message.id ? "Copied" : "Copy"}
          >
            <Icon name={copiedId === message.id ? "check" : "copy"} size={15} />
          </button>
          {prompt && (
            <>
              {/* "Ask again" and not "Regenerate": the API has no way to
                  replace a turn, so this asks the same question once more and
                  the new exchange is appended. Showing it in place would claim
                  a history Microsoft does not have. */}
              <button
                type="button"
                disabled={busy}
                onClick={() => onAsk(prompt)}
                aria-label="Ask this question again"
                title="Ask again"
              >
                <Icon name="refresh" size={15} />
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onEditQuestion(prompt)}
                aria-label="Edit this question"
                title="Edit question"
              >
                <Icon name="pencil" size={15} />
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}
